// components/slice/cart-slice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "./store"; // Adjust path if needed

type CartData = any; // Replace with specific cart structure if available

type CartState = {
  carts: {
    [organisationId: string]: {
      [accessPointId: string]: CartData;
    };
  };
  activeCart: any;
};

const initialState: CartState = {
  carts: {},
  activeCart: null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCarts: (state, action) => {
      state.carts = action.payload;
    },
    setActiveCart: (state, action) => {
      state.activeCart = action.payload;
    },
    clearCarts: (state) => {
      state.carts = {};
      state.activeCart = null;
    },
    removeCart: (state, action) => {
      const { organisationId, accessPointId } = action.payload;
      if (state.carts[organisationId]?.[accessPointId]) {
        delete state.carts[organisationId][accessPointId];

        // Clean up empty organisation entry
        if (Object.keys(state.carts[organisationId]).length === 0) {
          delete state.carts[organisationId];
        }
      }
    },
  },
});

// ✅ Thunk to update or add a cart
export const updateCart = (
  organisationId: string,
  accessPointId: string,
  cart: CartData
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { carts } = getState().cart;

    const updatedCarts = {
      ...carts,
      [organisationId]: {
        ...(carts[organisationId] || {}),
        [accessPointId]: cart,
      },
    };
    dispatch(setActiveCart(cart));
    dispatch(setCarts(updatedCarts));
  };
};

// Export actions
export const { setCarts, setActiveCart, clearCarts, removeCart } =
  cartSlice.actions;

// Optional selectors
export const getAllCarts = (state: RootState) => state.cart.carts;

export const getCartByOrgAndAccessPoint = (
  state: RootState,
  organisationId: string,
  accessPointId: string,
  cart: any = {}
) => {
  return state.cart?.carts[organisationId]?.[accessPointId] || cart;
};

export const getActiveCart = (
  state: RootState,
) => {
  return state.cart?.activeCart || null;
};

// Export reducer
export default cartSlice.reducer;
